import * as React from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, useMediaQuery } from '@mui/material';
import { useNotify } from 'react-admin';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import axiosInstance from '../../core/axiosConfig';
import { getBackendLanguage } from '../../core/i18n';
import { PageHeader } from '../page-header/PageHeader';
import { BackButton } from '../back-button/BackButton';
import { EmptyBox } from '../empty-box/EmptyBox';
import { IconButton } from '../icon-button/IconButton';
import { DraggableList, type DraggableListRow } from './draggableList';
import * as Sentry from '@sentry/react';
import { pageGutterSx } from '../../core/theme/layout';

/**
 * Category order only — dish order lives on its own screen
 * (menuItemOrderPage), reached per-category via the "Dania" action below.
 * Keeping the two apart means this list never has to render a category's
 * dishes just to let you reorder the categories themselves.
 */
export const MenuOrderPage = () => {
  const { id } = useParams();
  const routerLocation = useLocation();
  const locationName = (routerLocation.state as { locationName?: string } | null)?.locationName;
  const { t } = useTranslation();
  const notify = useNotify();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width:600px)');
  const backendLanguage = getBackendLanguage();

  const [categories, setCategories] = React.useState<DraggableListRow[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);

  const loadData = React.useCallback(async () => {
    if (!id) return;

    setIsLoading(true);
    try {
      const res = await axiosInstance.get('/category/list', { params: { locationId: id, language: backendLanguage } });

      // "Inne" / "Others" is an auto-created bucket for unassigned products,
      // hidden from category management everywhere else — keep it hidden here too.
      const loaded: DraggableListRow[] = res.data
        .filter((category: any) => !category.isDefault)
        .map((category: any) => ({ id: category.id, name: category.name }));

      setCategories(loaded);
    } catch (e) {
      console.error('Failed to load categories', e);
      Sentry.captureException(e);
      notify(t('menuOrder.loadFailed'), { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [id, backendLanguage, notify, t]);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const handlePersist = React.useCallback(
    async (orderedCategoryIds: number[]) => {
      await axiosInstance.patch('/category/reorder', {
        locationId: Number(id),
        orderedCategoryIds,
      });
    },
    [id],
  );

  return (
    <Box sx={{ pb: 'var(--space-8)' }}>
      <PageHeader back={<BackButton />} title={t('menuOrder.title')} subtitle={locationName} />
      <Box sx={pageGutterSx}>
        {isLoading ? null : categories.length === 0 ? (
          <EmptyBox
            title={t('menuOrder.emptyBox.title')}
            description={t('menuOrder.emptyBox.description')}
            showCreate={false}
          />
        ) : (
          <DraggableList
            initialRows={categories}
            onPersist={handlePersist}
            saveErrorMessage={t('menuOrder.saveFailed')}
            renderAction={(category) => (
              <IconButton
                icon={<ChevronRightIcon fontSize="small" />}
                label={t('menuOrder.viewItems')}
                onClick={() =>
                  navigate(`/locationManagement/${id}/menu-order/${category.id}`, {
                    state: { locationName, categoryName: category.name },
                  })
                }
              />
            )}
          />
        )}
      </Box>
    </Box>
  );
};

export default MenuOrderPage;
